import { Group, Mesh, Quaternion, Vector3 } from '@anov/3d-core'
import type { CurvePath, Object3D } from '@anov/3d-core'
import ObjectLayer from '../Base/ObjectLayer'
import type { BatchInterface, Option } from './type'

export class Batch extends ObjectLayer implements BatchInterface {
  _mesh: Group
  _path: CurvePath<Vector3>
  _number: number
  _span: number
  _followTangent: boolean
  _entity: Object3D
  _option: any

  public get mesh() {
    return this._mesh
  }

  public get path(): CurvePath<Vector3> {
    return this._path
  }

  public set path(newValue: CurvePath<Vector3>) {
    if (newValue === this._path)
      return
    this._path = newValue
    this.handlePropertyUpdate()
  }

  public get number(): number {
    return this._number
  }

  public set number(newValue: number) {
    if (newValue === this._number)
      return
    this._number = Math.max(1, newValue)
    this.handlePropertyUpdate()
  }

  public get span(): number {
    return this._span
  }

  public set span(newValue: number) {
    if (newValue === this._span)
      return
    this._span = newValue > 0 ? newValue : 10
    this.handlePropertyUpdate()
  }

  public get followTangent(): boolean {
    return this._followTangent
  }

  public set followTangent(newValue: boolean) {
    if (newValue === this._followTangent)
      return
    this.handlePropertyUpdate()
  }

  constructor(option: Option, sceneControl?: any) {
    super(sceneControl)

    const { path, number, span, entity, followTangent } = option
    this._path = path
    this._number = number
    this._span = span
    this._followTangent = followTangent
    this._entity = entity
    this._option = option

    this.init()
  }

  private init() {
    // 判断是否闭合
    const isClosed = this.isEffectivelyClosed(this._path)

    let points = []
    if (this.number > 0)
      points = this.getAdaptiveSpacedPoints(this.path, this.number)
    else if (this.span && this.span > 0)
      points = this.getPointsAtDistance(this.path, this.span)

    this._mesh = new Group()

    // 模型原始旋转（转换为四元数后叠加）
    const originalEuler = this._entity.rotation.clone()
    const rotationQuaternion = new Quaternion()
      .setFromEuler(originalEuler)
      // 预创建可复用对象减少内存分配
    const _tempTangent = new Vector3()
    const _lookAtTarget = new Vector3()
    points?.forEach((point: Vector3, index) => {
      const proxy = this._entity.clone()
      proxy.visible = true
      proxy.position.add(point)

      if (this._followTangent) {
      // 重置旋转
        proxy.quaternion.identity()
        // 计算当前点在曲线上的参数化位置 (0到1)
        const t = index / Math.max(1, points.length - 1)
        // 获取精确的切线方向（自动归一化）
        this._path.getTangent(t, _tempTangent)
        // 计算看向目标点：当前位置 + 切线方向
        _lookAtTarget.copy(point).add(_tempTangent)
        // 设置模型朝向（默认使用 THREE.Object3D 的默认 UP 方向）
        proxy.lookAt(_lookAtTarget)
        // 在局部坐标系叠加旋转
        proxy.quaternion.multiply(rotationQuaternion)
      }
      this._mesh.add(proxy)
    })

    this.add(this._mesh)
  }

  private isEffectivelyClosed(curvePath, tolerance = 0.01) {
    if (curvePath.closed)
      return true

    // 获取第一个点和最后一个点
    const startPoint = curvePath.getPoint(0)
    const endPoint = curvePath.getPoint(1)

    // 计算距离
    const distance = startPoint.distanceTo(endPoint)

    // 如果距离小于容差，则认为是闭合的
    return distance < tolerance
  }

  /**
 * 从曲线路径上每隔指定距离采样点
 * @param {THREE.CurvePath} curvePath - Three.js的CurvePath对象（可以是曲线或直线组合）
 * @param {Number} distance - 采样点之间的距离（单位：米）
 * @return {Array<THREE.Vector3>} 采样点坐标数组
 */
  private getPointsAtDistance(curvePath, distance) {
  // 确保参数有效
    if (!curvePath || typeof distance !== 'number' || distance <= 0) {
      console.error('Invalid parameters')
      return []
    }

    const points = []

    // 获取曲线总长度
    const totalLength = curvePath.getLength()

    // 计算需要的采样点数量
    const numSamples = Math.floor(totalLength / distance) + 1

    // 第一个点总是加入（起点）
    points.push(curvePath.getPointAt(0))

    let currentU = 0
    // 使用二分查找法找到每个距离点对应的u参数
    for (let i = 1; i < numSamples; i++) {
      const targetDistance = i * distance

      // 从上一个点开始查找
      let low = currentU
      let high = 1
      let mid, tempDistance

      // 二分查找法查找对应的u值
      while (high - low > 0.0001) {
        mid = (low + high) / 2
        tempDistance = curvePath.getLength() * mid

        if (tempDistance < targetDistance)
          low = mid
        else
          high = mid
      }

      currentU = (low + high) / 2

      // 如果已达曲线末端，则退出循环
      if (currentU >= 1)
        break

      // 获取点并添加到结果数组
      const point = curvePath.getPointAt(currentU)
      points.push(point)
    }

    // 确保添加最后一个点（如果还没添加）
    if (currentU < 1)
      points.push(curvePath.getPointAt(1))

    return points
  }

  // 创建自适应采样点获取方法（处理count=1的特殊情况）
  private getAdaptiveSpacedPoints(curve: CurvePath<Vector3>, count: number) {
    if (count <= 0)
      return []

    // 当请求1个点时返回路径中点
    if (count === 1) {
      const midPoint = new Vector3()
      curve.getPointAt(0.5, midPoint) // 获取50%位置的点
      return [midPoint]
    }

    // 常规情况返回等间距点
    return curve.getSpacedPoints(count - 1)
  }

  // 统一属性更新入口
  private handlePropertyUpdate() {
    this.clearScene()
    this.init()
  }

  // 内存安全清理
  private clearScene() {
    this._mesh.traverse((child) => {
      if (child instanceof Mesh) {
        child.geometry?.dispose()
        Array.isArray(child.material)
          ? child.material.forEach(m => m.dispose())
          : child.material?.dispose()
      }
    })
    this.remove(this._mesh)
  }

  // 显式销毁方法
  dispose() {
    this.clearScene()
  }
}